import { createRouter, RouterProvider } from '@tanstack/react-router';
import { addLocale, locale } from 'primereact/api';
import { useMountEffect } from 'primereact/hooks';
import { routeTree } from '~/routeTree.gen';
import { PWAUpdatePrompt } from './components/ui/common/PWAUpdatePrompt';
import { NotFoundPage } from './components/ui/common/NotFoundPage';

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: ({ children }) => {
    return <>{children}</>;
  },
  defaultPendingMs: 0,
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: () => <NotFoundPage />,
});
// Register things for typesafety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const onInit = async () => {
  if (String(process.env.REACT_USE_MOCK_API) == 'true') {
    const { worker } = await import('./mocks/browser');
    await worker.start();
  }
};

// Map of supported locales to their dynamic imports
const localeImports: Record<
  string,
  () => Promise<{ default: Record<string, unknown> }>
> = {
  en: () => import('primelocale/en.json'),
  'en-gb': () => import('primelocale/en-gb.json'),
  'en-au': () => import('primelocale/en-au.json'),
};

export const RouterWrapper = () => {
  const browserLocale = navigator.languages?.[0] || navigator.language;
  useMountEffect(() => {
    const initializeMocks = async () => onInit();
    const importLocales = async () => {
      try {
        const normalizedLocale = browserLocale.toLowerCase();
        const localeKeys = Object.keys(localeImports);

        // Find matching locale: exact match, then language prefix match
        let selectedLocale = 'en';
        const exactMatch = localeKeys.find((k) => k === normalizedLocale);
        if (exactMatch) {
          selectedLocale = exactMatch;
        } else {
          const prefixMatch = localeKeys.find(
            (k) =>
              k.split('-')[0] === normalizedLocale.split('-')[0] ||
              k === normalizedLocale.split('-')[0],
          );
          if (prefixMatch) {
            selectedLocale = prefixMatch;
          }
        }

        const loadedLocale = await localeImports[selectedLocale]();
        const def = loadedLocale.default;
        addLocale(
          selectedLocale,
          def[selectedLocale] as Record<string, unknown>,
        );
        locale(selectedLocale);
      } catch (error) {
        console.warn('Failed to load locale:', error);
      }
    };

    initializeMocks();
    importLocales();
  });

  return (
    <>
      <PWAUpdatePrompt />
      <RouterProvider router={router} />
    </>
  );
};
